// How a refused model enumeration READS (arc 101 slice 02). `lib/keys.ts` owns
// the POST; this owns the one thing that changes for a different reason — the
// copy a refusal is framed with.
//
// **Branch on the machine half, never on the prose.** The API answers a failed
// enumeration with 422 + a `reason` extension precisely so a client does not
// regex `detail`, and the eight reasons are not one thing: `not_enumerable` is
// permanent and asking again cannot help, `unreachable` is worth retrying, and
// `empty_list` means the credential is FINE and there is simply nothing to pick.
// Rendering those three the same way sends a maintainer off to re-check a key
// that already works.
//
// The server's own `detail` rides along and is printed under the framing line —
// the same posture every other refusal surface in this app takes (the refusals
// are the API's; we frame them, we do not re-derive them).

import { ApiError } from './api';

/** A framing copy key, its interpolations, and the API's own sentence. */
export type ProbeRefusal = {
  readonly key: string;
  readonly vars?: Readonly<Record<string, string | number>>;
  readonly detail: string | null;
};

/**
 * `reason` → copy key. The vocabulary is `ModelsListFault`
 * (`packages/billing/src/models-list.ts`): the transport's five, plus
 * `not_enumerable`, `unrecognized_body` and `empty_list`. `http_error` is absent
 * here because it alone spends a second extension — see below.
 */
const REASON_KEYS: Readonly<Record<string, string>> = {
  not_enumerable: 'account_keys.probe_not_enumerable',
  unreachable: 'account_keys.probe_unreachable',
  empty_list: 'account_keys.probe_empty_list',
  no_base_url: 'account_keys.probe_no_base_url',
  oversized_body: 'account_keys.probe_oversized_body',
  unparseable_body: 'account_keys.probe_unparseable_body',
  unrecognized_body: 'account_keys.probe_unrecognized_body',
};

/**
 * The line a refused probe renders.
 *
 * A 400 is the endpoint: the route answers `byokInvalidBaseUrl` for an SSRF or
 * wire refusal and a Zod 400 for a malformed body, and both mean "fix what you
 * typed". It is framed by STATUS rather than by problem `type` because `type` is
 * a standard RFC 9457 member — `problemExtensions` filters it out, so `ApiError`
 * never carries it (the `lib/task-refusal.ts` rule).
 *
 * A 429 is the per-account enumeration budget (20/min), which is a wait, not a
 * broken credential. Everything else — a 5xx, a dead socket, a body this client
 * cannot read — is the generic line plus whatever the server said.
 */
export function probeRefusal(error: unknown): ProbeRefusal {
  if (!(error instanceof ApiError)) return { key: 'account_keys.probe_failed', detail: null };
  const detail = error.detail ?? null;
  if (error.status === 400) return { key: 'account_keys.probe_invalid_base_url', detail };
  if (error.status === 429) return { key: 'account_keys.probe_rate_limited', detail };

  const reason = error.extensions?.['reason'];
  if (typeof reason !== 'string') return { key: 'account_keys.probe_failed', detail };
  // The provider's own status is the actionable half of an `http_error` — a 401
  // is a bad key and a 404 is a bad endpoint. A refusal that carried no numeric
  // status still reads as the provider refusing, never as a generic failure:
  // absent detail must not collapse a class we know the name of.
  if (reason === 'http_error') {
    const status = error.extensions?.['provider_status'];
    return typeof status === 'number'
      ? { key: 'account_keys.probe_http_error_status', vars: { status }, detail }
      : { key: 'account_keys.probe_http_error', detail };
  }
  return { key: REASON_KEYS[reason] ?? 'account_keys.probe_failed', detail };
}
